import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { EnvironmentConfigService } from '../environment-config.service';

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

@Injectable({
  providedIn: 'root'
})
export class CustomAnalyticsService {
  private gtmId: string;
  private isInitialized = false;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private envConfig: EnvironmentConfigService
  ) {
    const prodGtmId = 'GTM-595QLH3C';
    const nonProdGtmId = 'GTM-TJJ7JG2M';

    if (this.envConfig.currentUiEnvironment == 'prod') {
      this.gtmId = prodGtmId;
    } else {
      this.gtmId = nonProdGtmId;
    }
  }

  initializeGtm(): void {
    if (this.isInitialized || !this.gtmId) {
      return;
    }

    this.ensureDataLayer();
    this.addGtmScriptToHead();
    this.addGtmNoscriptToBody();
    this.isInitialized = true;
  }

  private ensureDataLayer(): void {
    if (!window.dataLayer) {
      window.dataLayer = [];
    }
  }

  //inject gtm script tag
  private addGtmScriptToHead(): void {
    // Add HTML comment before the script
    const commentStart = this.document.createComment(' Google Tag Manager ');
    const commentEnd = this.document.createComment(' End Google Tag Manager ');

    // Create the inline GTM initialization script
    const inlineScript = this.document.createElement('script');
    inlineScript.type = 'text/javascript';
    inlineScript.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${this.gtmId}');`;

    const firstHeadElement = this.document.head.firstChild;

    if (firstHeadElement) {
      // Insert before the first element in head
      this.document.head.insertBefore(commentStart, firstHeadElement);
      this.document.head.insertBefore(inlineScript, firstHeadElement);
      this.document.head.insertBefore(commentEnd, firstHeadElement);
    } else {
      // If head is empty, just append
      this.document.head.appendChild(commentStart);
      this.document.head.appendChild(inlineScript);
      this.document.head.appendChild(commentEnd);
    }
  }

  private addGtmNoscriptToBody(): void {
    const noscript = this.document.createElement('noscript');
    const iframe = this.document.createElement('iframe');

    iframe.src = `https://www.googletagmanager.com/ns.html?id=${this.gtmId}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';

    noscript.appendChild(iframe);

    // Insert the noscript as the first element in the body
    if (this.document.body.firstChild) {
      this.document.body.insertBefore(noscript, this.document.body.firstChild);
    } else {
      this.document.body.appendChild(noscript);
    }
  }

  trackEvent(eventName: string, payload: Record<string, any> = {}): void {
    this.ensureDataLayer();
    window.dataLayer.push({
      event: eventName,
      ...payload
    });
  }

  pageView(
    page_location: string,
    page_title?: string,
    page_path?: string
  ): void {
    this.trackEvent('page_view', {
      page_location,
      page_title,
      page_path
    });
  }

  setUserId(userId?: string | null): void {
    this.ensureDataLayer();
    if (userId) {
      window.dataLayer.push({ user_id: userId });
    } else {
      window.dataLayer.push({ user_id: null });
    }
  }

  clearUserId(): void {
    this.setUserId(null);
  }
}
